import { ActionIcon, Button, Modal } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { Link } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import type { TemplateMeta } from "../../shared/templates";
import type { ResumeDocument } from "../api/client";
import { useMessages } from "../i18n/provider";
import styles from "./template-preview.module.css";
import { TemplateThumb } from "./template-thumb";

export function TemplatePreview({
  template,
  doc,
  useHref,
  onClose,
  onPrevious,
  onNext,
}: {
  template: TemplateMeta;
  doc: ResumeDocument;
  useHref: string;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const t = useMessages();
  const name = t(template.nameKey as MessageKey);

  useHotkeys([
    ["ArrowLeft", onPrevious],
    ["ArrowRight", onNext],
  ]);

  return (
    <Modal.Root opened onClose={onClose} fullScreen transitionProps={{ duration: 200 }}>
      <Modal.Overlay />
      <Modal.Content radius={0} p={0} aria-label={name} className={styles.content}>
        <Modal.Body className={styles.body} p={0}>
          <ActionIcon
            className={styles.close}
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <IconX size={20} stroke={1.5} />
          </ActionIcon>

          <div className={styles.stage}>
            <ActionIcon
              className={styles.nav}
              variant="light"
              color="gray"
              size="xl"
              radius="xl"
              aria-label={t("templates.previous")}
              onClick={onPrevious}
            >
              <IconChevronLeft size={22} stroke={1.5} />
            </ActionIcon>

            <div className={styles.page} key={template.id}>
              <TemplateThumb doc={doc} />
            </div>

            <ActionIcon
              className={styles.nav}
              variant="light"
              color="gray"
              size="xl"
              radius="xl"
              aria-label={t("templates.next")}
              onClick={onNext}
            >
              <IconChevronRight size={22} stroke={1.5} />
            </ActionIcon>
          </div>

          <div className={styles.meta}>
            <h2 className={styles.title}>{name}</h2>
            <p className={styles.description}>{t(template.descriptionKey as MessageKey)}</p>
            <Button component={Link} to={useHref} size="md" mt="lg">
              {t("templates.use")}
            </Button>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
